/**
 * Trust-Drip Card Component
 *
 * Displays permission request cards when SuperJarvis needs access
 * to external services (Gmail, Calendar, etc.)
 * 
 * Key principles:
 * - Just-in-time: Shows only when actually needed in conversation
 * - Contextual: Explains WHY we're asking based on current task
 * - Benefit-focused: Shows the value to the user
 * - Non-intrusive: User can decline without breaking the flow
 */
import React from 'react';

// Provider configurations with benefits messaging
const PROVIDER_CONFIG: Record<string, {
  name: string;
  icon: string;
  gradient: string;
  benefits: string[];
}> = {
  gmail: {
    name: 'Gmail',
    icon: '✉️',
    gradient: 'from-red-500 to-red-600',
    benefits: [
      'Organize and prioritize your emails automatically',
      'Draft responses and send on your behalf',
      'Find important messages instantly'
    ]
  },
  calendar: {
    name: 'Google Calendar',
    icon: '📅',
    gradient: 'from-blue-500 to-blue-600',
    benefits: [
      'Add events and reservations automatically',
      'Set reminders so you never miss appointments',
      'Check your availability for scheduling'
    ]
  },
  drive: {
    name: 'Google Drive',
    icon: '📁',
    gradient: 'from-yellow-500 to-yellow-600',
    benefits: [
      'Access and organize your files',
      'Create documents and spreadsheets',
      'Share files with others'
    ]
  },
  github: {
    name: 'GitHub',
    icon: '🐙',
    gradient: 'from-gray-600 to-gray-800',
    benefits: [
      'Manage your repositories',
      'Create issues and pull requests',
      'Review and merge code'
    ]
  },
  slack: {
    name: 'Slack',
    icon: '💬',
    gradient: 'from-purple-500 to-purple-600',
    benefits: [
      'Send messages to your team',
      'Stay updated on conversations',
      'Manage channels and notifications'
    ]
  },
  notion: {
    name: 'Notion',
    icon: '📝',
    gradient: 'from-gray-700 to-gray-900',
    benefits: [
      'Create and update pages',
      'Manage your databases',
      'Organize your workspace'
    ]
  },
  travel: {
    name: 'Travel Accounts',
    icon: '✈️',
    gradient: 'from-cyan-500 to-blue-600',
    benefits: [
      'Access exclusive member fares and loyalty pricing',
      'Apply your frequent flyer miles automatically',
      'Pre-fill passenger details for faster checkout'
    ]
  },
  flights: {
    name: 'Flight Booking',
    icon: '🛫',
    gradient: 'from-cyan-400 to-cyan-600',
    benefits: [
      'Compare prices across your connected airlines',
      'Use saved payment methods for faster checkout',
      'Track your booking and get real-time updates'
    ]
  },
  hotels: {
    name: 'Hotel Accounts',
    icon: '🏨',
    gradient: 'from-indigo-500 to-purple-600',
    benefits: [
      'Access member-only rates and loyalty perks',
      'Apply reward points automatically',
      'Pre-fill guest preferences'
    ]
  }
};

interface TrustDripCardProps {
  provider: string;
  context: string;  // The current conversation context (e.g., "booking a reservation")
  customMessage?: string;  // Optional custom message from backend
  onGrant: () => void;
  onDeny: () => void;
}

export const TrustDripCard: React.FC<TrustDripCardProps> = ({
  provider,
  context,
  customMessage,
  onGrant,
  onDeny
}) => {
  const config = PROVIDER_CONFIG[provider.toLowerCase()] || {
    name: provider,
    icon: '🔗',
    gradient: 'from-cyan-500 to-cyan-600',
    benefits: ['Access this service on your behalf']
  };

  // Generate contextual message based on what user is trying to do
  const getContextualMessage = () => {
    if (customMessage) return customMessage;
    
    const ctx = context.toLowerCase();
    
    if (provider === 'calendar') {
      if (ctx.includes('reservation') || ctx.includes('booking')) {
        return "Would you like me to add this reservation to your calendar? I can set a reminder so you won't forget.";
      }
      if (ctx.includes('meeting') || ctx.includes('schedule')) {
        return "I can add this to your calendar and send invites to attendees. Want me to help you stay organized?";
      }
      return "I can help manage your schedule. Want me to connect to your calendar?";
    }
    
    if (provider === 'gmail') {
      if (ctx.includes('email') || ctx.includes('organize')) {
        return "I can help you sort through your emails and highlight what's important. Ready to take control of your inbox?";
      }
      if (ctx.includes('send') || ctx.includes('reply')) {
        return "I can draft and send emails for you. Want me to handle your correspondence?";
      }
      return "I can help manage your emails more efficiently. Want to connect?";
    }
    
    return `Connect ${config.name} to unlock more capabilities.`;
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        onClick={onDeny}
      />
      
      {/* Card */}
      <div className="relative z-10 w-full max-w-md bg-black/95 backdrop-blur-2xl border border-cyan-400/30 rounded-2xl overflow-hidden shadow-[0_0_60px_rgba(34,211,238,0.3)] animate-in slide-in-from-bottom-4 duration-300">
        
        {/* Header with gradient icon */}
        <div className="p-6 pb-4 flex items-center space-x-4">
          <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${config.gradient} flex items-center justify-center shadow-lg`}>
            <span className="text-2xl">{config.icon}</span>
          </div>
          <div>
            <h3 className="text-xl font-bold text-cyan-300 drop-shadow-[0_0_10px_rgba(34,211,238,0.5)]">
              Connect {config.name}?
            </h3>
            <p className="text-cyan-400/50 text-sm">SuperJarvis needs your permission</p>
          </div>
        </div>
        
        {/* Contextual message - the WHY */}
        <div className="px-6 pb-4">
          <p className="text-cyan-200/90 text-base leading-relaxed">
            {getContextualMessage()}
          </p>
        </div>
        
        {/* Benefits - the VALUE */}
        <div className="mx-6 mb-4 p-4 bg-cyan-400/[0.05] border border-cyan-400/20 rounded-xl">
          <p className="text-cyan-400/60 text-xs font-semibold uppercase tracking-wider mb-3">
            With {config.name} connected, I can:
          </p>
          <ul className="space-y-2">
            {config.benefits.map((benefit, i) => (
              <li key={i} className="flex items-start space-x-3 text-sm text-cyan-300/80">
                <span className="text-cyan-400 mt-0.5">✓</span>
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Actions */}
        <div className="p-6 pt-2 flex gap-3">
          <button
            onClick={onDeny}
            className="flex-1 py-3 px-4 rounded-xl bg-transparent border border-cyan-400/30 text-cyan-400/70 font-semibold hover:bg-cyan-400/10 hover:text-cyan-300 hover:border-cyan-400/50 transition-all duration-200"
          >
            Not Now
          </button>
          <button
            onClick={onGrant}
            className="flex-1 py-3 px-4 rounded-xl bg-cyan-400 text-black font-semibold shadow-[0_0_25px_rgba(34,211,238,0.5)] hover:shadow-[0_0_35px_rgba(34,211,238,0.7)] hover:scale-[1.02] transition-all duration-200"
          >
            Connect
          </button>
        </div>
        
        {/* Disclaimer */}
        <div className="px-6 pb-4 text-center">
          <p className="text-cyan-400/30 text-xs">
            You can disconnect anytime in Settings → Privacy
          </p>
        </div>
      </div>
    </div>
  );
};

export default TrustDripCard;
